import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Save, X, Plus, ShieldAlert } from 'lucide-react';
import { addGame, toggleOwned } from '../lib/gameService';
import { useAuth } from '../contexts/AuthContext';
import type { Game } from '../types';

// Corrigindo o tipo para adicionar Game
type NewGameData = Omit<Game, 'id' | 'created_at' | 'updated_at'>;

// Categorias de jogos comuns
const GAME_CATEGORIES = [
  'Estratégia', 'Familiar', 'Party Game', 'Aventura', 'Cooperativo',
  'Deckbuilding', 'Euro', 'Temático', 'Tático', 'RPG',
  'Card Game', 'Educativo', 'Abstrato', 'Infantil', 'Competitivo'
];

// Mecânicas de jogos comuns
const GAME_MECHANICS = [
  'Deck Building', 'Worker Placement', 'Area Control', 'Dice Rolling',
  'Card Drafting', 'Hand Management', 'Tile Placement', 'Set Collection',
  'Role Playing', 'Cooperative Play', 'Action Points', 'Auction/Bidding'
];

// Temas de jogos comuns
const GAME_THEMES = [
  'Medieval', 'Fantasia', 'Sci-Fi', 'Horror', 'Histórico',
  'Guerra', 'Econômico', 'Exploração', 'Civilização', 'Zombies'
];

const AddGamePage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addToCollection, setAddToCollection] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Verificar se o usuário é administrador
  useEffect(() => {
    // Usar a propriedade isAdmin do contexto
    if (!isAdmin) {
      navigate('/search', { 
        state: { 
          message: 'Apenas administradores podem adicionar novos jogos à base de dados.' 
        } 
      });
    }
  }, [isAdmin, navigate]);

  // Verificação adicional antes de renderizar o conteúdo
  if (!isAdmin) {
    return (
      <div className="container mx-auto max-w-4xl py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <ShieldAlert className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Acesso Restrito</h2>
          <p className="text-gray-600 mb-6">
            Apenas administradores podem adicionar novos jogos ao catálogo.
            Você será redirecionado para a página de busca.
          </p>
          <Link
            to="/search"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition inline-flex items-center"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Ir para Busca
          </Link>
        </div>
      </div>
    );
  }
  
  // Corrigindo o tipo do formData
  const [formData, setFormData] = useState<NewGameData>({
    name: '',
    year: null,
    ano_nacional: null,
    description: null,
    image_url: null,
    categories: [],
    mechanics: [],
    themes: [],
    artists: [],
    designers: [],
    min_players: null,
    max_players: null,
    play_time: null,
    complexity: null,
    bgg_id: null
  });
  
  // Estado para categoria personalizada
  const [customCategory, setCustomCategory] = useState('');
  const [customMechanic, setCustomMechanic] = useState('');
  const [customTheme, setCustomTheme] = useState('');
  const [customArtist, setCustomArtist] = useState('');
  const [customDesigner, setCustomDesigner] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'number') {
      setFormData({
        ...formData,
        [name]: value ? parseFloat(value) : null
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleCategoryToggle = (category: string) => {
    const currentCategories = formData.categories || [];
    
    if (currentCategories.includes(category)) {
      setFormData({
        ...formData,
        categories: currentCategories.filter(c => c !== category)
      });
    } else {
      setFormData({
        ...formData,
        categories: [...currentCategories, category]
      });
    }
  };

  const handleMechanicToggle = (mechanic: string) => {
    const currentMechanics = formData.mechanics || [];
    
    if (currentMechanics.includes(mechanic)) {
      setFormData({
        ...formData,
        mechanics: currentMechanics.filter(m => m !== mechanic)
      });
    } else {
      setFormData({
        ...formData,
        mechanics: [...currentMechanics, mechanic]
      });
    }
  };

  const handleThemeToggle = (theme: string) => {
    const currentThemes = formData.themes || [];
    
    if (currentThemes.includes(theme)) {
      setFormData({
        ...formData,
        themes: currentThemes.filter(t => t !== theme)
      });
    } else {
      setFormData({
        ...formData,
        themes: [...currentThemes, theme]
      });
    }
  };

  const handleAddCustomCategory = () => {
    if (customCategory.trim()) {
      const currentCategories = formData.categories || [];
      if (!currentCategories.includes(customCategory)) {
        setFormData({
          ...formData,
          categories: [...currentCategories, customCategory.trim()]
        });
      }
      setCustomCategory('');
    }
  };

  const handleAddCustomMechanic = () => {
    if (customMechanic.trim()) {
      const currentMechanics = formData.mechanics || [];
      if (!currentMechanics.includes(customMechanic)) {
        setFormData({
          ...formData,
          mechanics: [...currentMechanics, customMechanic.trim()]
        });
      }
      setCustomMechanic('');
    }
  };

  const handleAddCustomTheme = () => {
    if (customTheme.trim()) {
      const currentThemes = formData.themes || [];
      if (!currentThemes.includes(customTheme)) {
        setFormData({
          ...formData,
          themes: [...currentThemes, customTheme.trim()]
        });
      }
      setCustomTheme('');
    }
  };

  const handleAddCustomArtist = () => {
    if (customArtist.trim()) {
      const currentArtists = formData.artists || [];
      if (!currentArtists.includes(customArtist)) {
        setFormData({
          ...formData,
          artists: [...currentArtists, customArtist.trim()]
        });
      }
      setCustomArtist('');
    }
  };

  const handleAddCustomDesigner = () => {
    if (customDesigner.trim()) {
      const currentDesigners = formData.designers || [];
      if (!currentDesigners.includes(customDesigner)) {
        setFormData({
          ...formData,
          designers: [...currentDesigners, customDesigner.trim()]
        });
      }
      setCustomDesigner('');
    }
  };

  const validateForm = (): boolean => {
    // Validação básica
    if (!formData.name) {
      setError('O nome do jogo é obrigatório');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Garantir que os arrays estejam inicializados corretamente e explicitamente definidos
      const gameDataToAdd = {
        ...formData,
        artists: Array.isArray(formData.artists) ? formData.artists : [],
        designers: Array.isArray(formData.designers) ? formData.designers : [],
        categories: Array.isArray(formData.categories) ? formData.categories : [],
        mechanics: Array.isArray(formData.mechanics) ? formData.mechanics : [],
        themes: Array.isArray(formData.themes) ? formData.themes : []
      };
      
      console.log('Enviando dados para criação:', JSON.stringify(gameDataToAdd));
      
      // Adicionar jogo com os dados corrigidos
      const newGame = await addGame(gameDataToAdd);
      
      // Adicionar à coleção se a opção estiver marcada
      if (addToCollection) {
        await toggleOwned(user.id, newGame.id, true);
      }
      
      setSuccessMessage('Jogo adicionado com sucesso!');
      
      // Limpar formulário ou redirecionar
      setTimeout(() => {
        navigate(`/games/${newGame.id}`);
      }, 1500);
    } catch (err: any) {
      console.error('Erro ao adicionar jogo:', err);
      setError(err.message || 'Ocorreu um erro ao adicionar o jogo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto max-w-4xl py-8">
      {/* Navegação */}
      <div className="mb-6">
        <Link 
          to="/collection"
          className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Voltar para a Coleção
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Adicionar Novo Jogo</h1>
          
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
              {successMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Informações Básicas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Jogo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ano de Publicação
                    </label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year || ''}
                      onChange={handleChange}
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ano de Lançamento no Brasil
                    </label>
                    <input
                      type="number"
                      name="ano_nacional"
                      value={formData.ano_nacional || ''}
                      onChange={handleChange}
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL da Imagem
                    </label>
                    <input
                      type="url"
                      name="image_url"
                      value={formData.image_url || ''}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Categorias */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Categorias</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {GAME_CATEGORIES.map(category => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => handleCategoryToggle(category)}
                      className={`px-3 py-1 text-sm rounded-full ${
                        formData.categories?.includes(category)
                          ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="Categoria personalizada"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomCategory}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                    disabled={!customCategory.trim()}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formData.categories && formData.categories.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Categorias selecionadas: {formData.categories.join(', ')}
                  </div>
                )}
              </div>
              
              {/* Mecânicas */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Mecânicas</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {GAME_MECHANICS.map(mechanic => (
                    <button
                      key={mechanic}
                      type="button"
                      onClick={() => handleMechanicToggle(mechanic)}
                      className={`px-3 py-1 text-sm rounded-full ${
                        formData.mechanics?.includes(mechanic)
                          ? 'bg-teal-100 text-teal-800 border border-teal-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {mechanic}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customMechanic}
                    onChange={(e) => setCustomMechanic(e.target.value)}
                    placeholder="Mecânica personalizada"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomMechanic}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                    disabled={!customMechanic.trim()}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formData.mechanics && formData.mechanics.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Mecânicas selecionadas: {formData.mechanics.join(', ')}
                  </div>
                )}
              </div>
              
              {/* Temas */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Temas</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {GAME_THEMES.map(theme => (
                    <button
                      key={theme}
                      type="button"
                      onClick={() => handleThemeToggle(theme)}
                      className={`px-3 py-1 text-sm rounded-full ${
                        formData.themes?.includes(theme)
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customTheme}
                    onChange={(e) => setCustomTheme(e.target.value)}
                    placeholder="Tema personalizado"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomTheme}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                    disabled={!customTheme.trim()}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formData.themes && formData.themes.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Temas selecionados: {formData.themes.join(', ')}
                  </div>
                )}
              </div>
              
              {/* Artistas e Designers */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Créditos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designers
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customDesigner}
                        onChange={(e) => setCustomDesigner(e.target.value)}
                        placeholder="Adicionar designer"
                        className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomDesigner}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                        disabled={!customDesigner.trim()}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    {formData.designers && formData.designers.length > 0 && (
                      <div className="mt-2">
                        <ul className="list-disc pl-5 text-sm text-gray-600">
                          {formData.designers.map((designer, index) => (
                            <li key={index}>{designer}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Artistas
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customArtist}
                        onChange={(e) => setCustomArtist(e.target.value)}
                        placeholder="Adicionar artista"
                        className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomArtist}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                        disabled={!customArtist.trim()}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    {formData.artists && formData.artists.length > 0 && (
                      <div className="mt-2">
                        <ul className="list-disc pl-5 text-sm text-gray-600">
                          {formData.artists.map((artist, index) => (
                            <li key={index}>{artist}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Detalhes do Jogo */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Detalhes do Jogo</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número Mínimo de Jogadores
                    </label>
                    <input
                      type="number"
                      name="min_players"
                      value={formData.min_players || ''}
                      onChange={handleChange}
                      min="1"
                      max="99"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número Máximo de Jogadores
                    </label>
                    <input
                      type="number"
                      name="max_players"
                      value={formData.max_players || ''}
                      onChange={handleChange}
                      min="1"
                      max="99"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tempo de Jogo (minutos)
                    </label>
                    <input
                      type="number"
                      name="play_time"
                      value={formData.play_time || ''}
                      onChange={handleChange}
                      min="1"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complexidade (1-5)
                    </label>
                    <input
                      type="number"
                      name="complexity"
                      value={formData.complexity || ''}
                      onChange={handleChange}
                      min="1"
                      max="5"
                      step="0.1"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID no BoardGameGeek (opcional)
                    </label>
                    <input
                      type="number"
                      name="bgg_id"
                      value={formData.bgg_id || ''}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows={5}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                ></textarea>
              </div>
              
              {/* Opção de adicionar à coleção */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="addToCollection"
                  checked={addToCollection}
                  onChange={(e) => setAddToCollection(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="addToCollection" className="ml-2 text-sm text-gray-700">
                  Adicionar este jogo à minha coleção
                </label>
              </div>
              
              {/* Botões de ação */}
              <div className="flex justify-end space-x-4">
                <Link
                  to="/collection"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition flex items-center"
                >
                  <X className="h-5 w-5 mr-1" />
                  Cancelar
                </Link>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-1 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="h-5 w-5 mr-1" />
                      Salvar Jogo
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddGamePage; 